import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";
import { FileSystemService } from "../../../../../../../swaggerServices";
import { ClientNotificationService } from "../../../../../client-notification.service";

export interface CommentValue {
  message: string;
  attachments: Map<string, File>;
}

@Component({
    selector: 'app-attachments',
    templateUrl: './attachments.component.html',
    styleUrls: ['./attachments.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AttachmentsComponent),
            multi: true
        }],
    standalone: false
})
export class AttachmentsComponent implements OnInit, ControlValueAccessor {
  constructor(
    private fileSystemService: FileSystemService,
    private translocoService: TranslocoService,
    private clientNotificationService: ClientNotificationService,
  ) { }

  @Input() placeholder: string = "Type your comment here...";
  @Input() disableComponent = null;
  @Input() disableAttachments = null;
  @Input() fileTypes: string[] = [];
  @Output() input: EventEmitter<string> = new EventEmitter<string>();
  @Output() setModal: EventEmitter<string> = new EventEmitter<string>();
  title = "";
  value: CommentValue = {
    message: "",
    attachments: new Map(),
  };
  isDragOver = false;
  disabled = this.disableComponent || this.disableAttachments;

  ngOnInit() {
    this.translocoService
      .selectTranslate("attachments")
      .subscribe(value => (this.title = value));
  }

  onChange = (value: CommentValue) => { };
  onTouched = () => { };


  writeValue(value?: CommentValue | null): void {
    this.value = value ?? {
      message: '',
      attachments: new Map(),
    };
  }

  registerOnChange(fn: (value: CommentValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onInput(event: Event) {
    const message = (event.target as HTMLTextAreaElement).value;
    this.value.message = message;
    this.input.emit(message);
    this.onChange(this.value);
  }


  private isFileDrag(event: DragEvent): boolean {
    return !!event.dataTransfer &&
      Array.from(event.dataTransfer.types).includes('Files');
  }


  onDragEnter(event: DragEvent): void {
    if (this.disabled) return;
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;
    if (!this.isFileDrag(event)) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    if (
      event.clientX <= rect.left ||
      event.clientX >= rect.right ||
      event.clientY <= rect.top ||
      event.clientY >= rect.bottom
    ) {
      this.isDragOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    if (!this.isFileDrag(event)) return;
    event.preventDefault();
    this.isDragOver = false;

    let files = Array.from(event.dataTransfer?.files);
    if (!files || files.length === 0) return;
    this.handleAttachments(files)
  }

  private handleAttachments(files: File[]) {
    for (const file of files) {
      if (this.value.attachments.size >= 5) return;

      if (!this.isAllowedFileType(file)) {
        this.clientNotificationService.appendMessage("Attachment error", `File type not allowed: ${file.name}`, false);
        continue;
      }

      this.value.attachments.set(file.name, file);
    }
    this.onChange(this.value);
  }

  onPaste(event: ClipboardEvent) {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    let files = [];
    for (const item of Array.from(clipboard.items)) {
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const id = Math.random().toString(36).substring(2, 7);
        const renamedFile = new File([file], `screenshot-${id}.png`, { type: file.type });
        files.push(renamedFile);
      }
    }
    this.handleAttachments(files);
  }

  removeAttachment(name: string) {
    this.value.attachments.delete(name);
    this.onChange(this.value);
  }

  downloadAttachment(documentId: string): void {
    this.fileSystemService
      .fileSystemDownloadSingleFilePut({ documentId })
      .subscribe(({ fileContent, fileName }) => {

        const url = URL.createObjectURL(fileContent);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
      });
  }

  onClickAttachment(attachment: File) {
    if (!attachment.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      this.setModal.emit(event.target.result as string);
    };
    reader.readAsDataURL(attachment);
  }

  private isAllowedFileType(file: File): boolean {
    if (!this.fileTypes || this.fileTypes.length === 0) {
      return true;
    }

    return this.fileTypes.some(type => {
      if (type.includes('/')) {
        return file.type === type;
      }

      return file.name.toLowerCase().endsWith(type.toLowerCase());
    });
  }

}

